import type { MetaContent } from "@@/shared/meta-content/meta-content";
import type { Translatable } from "@@/shared/translations/translation";
import { svTranslator } from "@@/shared/translations/translation";
import { z } from "zod";

const maxResourceShortNameLength = 5;
/**
 * Optional short name, e.g. B12
 * if not set a short name will be generated based on
 * the title when needed. Max 5 chars.
 */
const resourceShortNameZodString = z.string().max(maxResourceShortNameLength);

/**
 * Optional short name, e.g. B12
 * if not set a short name will be generated based on
 * the title when needed. Max 5 chars.
 */
type ResourceShortName = z.infer<typeof resourceShortNameZodString>;

/**
 * Generates a short name for a resource.
 *
 * @param resource - The resource object for which to generate a short name.
 * @returns The generated short name for the resource.
 *
 * The function follows these steps:
 * 1. If the resource already has a short name, it returns it.
 * 2. Retrieves the Swedish title of the resource.
 * 3. Extracts any numbers from the end of the title.
 * 4. Removes the numbers from the title and trims any whitespace.
 * 5. Splits the title into words and takes the first letter of each word, joining them in uppercase.
 * 6. If the extracted number string is exactly `maxResourceShortNameLength` characters long, it returns the number string.
 * 7. If the number string is longer than `maxResourceShortNameLength`, it returns the first `maxResourceShortNameLength` characters of the number string.
 * 8. Combines the uppercase first letters and the number string to create a short name.
 * 9. If the combined short name is longer than `maxResourceShortNameLength`, it adjusts the length of the first letters to ensure the total length is `maxResourceShortNameLength`.
 * 10. Returns the final short name.
 */
const generateResourceShortName = (
    resource:
        | {
              shortName?: ResourceShortName;
              meta: MetaContent;
              title?: never;
          }
        | {
              shortName?: ResourceShortName;
              title: Translatable;
              meta?: never;
          },
): ResourceShortName => {
    // if short name is set already, return it
    if (resource.shortName) return resource.shortName;

    // get swedish title of the resource
    const title =
        svTranslator(
            "meta" in resource ? (resource.meta?.title ?? "") : resource.title,
        ) ?? "";

    // get all numbers from the end of the title
    const resourceNumber = title.match(/\d+$/)?.join("");

    // remove the numbers from the title
    const titleWithoutNumber = title.replace(/\d+$/, "").trim();

    // split the title into words
    const words = titleWithoutNumber.split(" ");

    // get the first letter of each word and join them in uppercase
    const firstLetters = words
        .map(word => (word.length > 0 ? word[0].toUpperCase() : ""))
        .join("");

    // if the number string is 5 (maxResourceShortNameLength) chars long, return it
    if (resourceNumber?.length === maxResourceShortNameLength)
        return resourceNumber;

    // if the number is longer than 5 chars, return the first 5 numbers as a string
    if (
        resourceNumber?.length &&
        resourceNumber.length > maxResourceShortNameLength
    ) {
        return resourceNumber.slice(0, maxResourceShortNameLength);
    }

    // join the first letters with the number string to create a short name
    const shortName = `${firstLetters ?? ""}${resourceNumber ?? ""}`;
    // if the short name is longer than 5 chars, return the first 5 letters
    if (shortName.length > maxResourceShortNameLength) {
        const numberLength = resourceNumber?.length ?? 0;
        const newFirstLetterLength = maxResourceShortNameLength - numberLength;
        const newFirstLetters = firstLetters.slice(0, newFirstLetterLength);
        return `${newFirstLetters ?? ""}${resourceNumber ?? ""}`;
    }
    // else just return the short name
    return shortName;
};

export { generateResourceShortName, resourceShortNameZodString };
export type { ResourceShortName };
